import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getFaqKey } from '../utils/keys';

const CTASection = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: t('faqQuestion1'),
      answer: t('faqAnswer1')
    },
    {
      question: t('faqQuestion2'),
      answer: t('faqAnswer2')
    },
    {
      question: t('faqQuestion3'),
      answer: t('faqAnswer3')
    },
    {
      question: t('faqQuestion4'),
      answer: t('faqAnswer4')
    },
    {
      question: t('faqQuestion5'),
      answer: t('faqAnswer5')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* FAQ Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('frequentlyAskedQuestions')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('quickAnswersToCommonQuestions')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={getFaqKey(faq.question, index)}
                className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                
                {openFaq === index && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed animate-fadeIn">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-logo-blue via-logo-cyan to-blue-600 rounded-3xl shadow-2xl overflow-hidden">
          <div className="px-8 py-16 lg:px-16 lg:py-20 text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              {t('readyToStartYourVisaApplication')}
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              {t('joinTravelersDescription')}
            </p>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 max-w-4xl mx-auto">
              <div className="flex items-center justify-center space-x-3 text-white">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span className="font-semibold">{t('successRate98')}</span>
              </div>
              <div className="flex items-center justify-center space-x-3 text-white">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span className="font-semibold">{t('expertSupport247')}</span>
              </div>
              <div className="flex items-center justify-center space-x-3 text-white">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span className="font-semibold">{t('fastProcessing')}</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={`https://wa.me/971585862103?text=Hi, I'm interested in visa services`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-logo-blue px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center space-x-2"
              >
                <span>💬</span>
                <span>{t('chatOnWhatsApp')}</span>
              </a>
              <a
                href="mailto:info@thevisagram.ae"
                className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-white/10 transition-all inline-flex items-center space-x-2"
              >
                <span>📧</span>
                <span>{t('emailUs')}</span>
              </a>
            </div>

            {/* Contact Info */}
            <div className="mt-8 text-white/80 text-sm">
              <p>{t('orCallUsDirectly')} <a href="tel:+971585862103" className="font-semibold text-white hover:underline">+971 58 586 2103</a></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
